import React, { useState, useEffect } from 'react';
import { UpdateInfo, openUpdateUrl, downloadAndInstallApk, type DownloadProgressCallback } from '../../services/update/updateService';
import { Capacitor } from '@capacitor/core';
import { ANIMATION_DURATION_MS, IOS_EASING } from '@ext-bookmark/shared/utils';

interface UpdateDialogProps {
  updateInfo: UpdateInfo;
  onClose?: () => void;
}

export const UpdateDialog: React.FC<UpdateDialogProps> = ({
  updateInfo,
  onClose,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // 进入动画
  useEffect(() => {
    // 延迟一帧确保动画能触发
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  const handleUpdate = async () => {
    if (!updateInfo.updateUrl) {
      setError('更新 URL 不可用');
      return;
    }

    const platform = Capacitor.getPlatform();
    
    // 检查 URL 是否是 APK 链接（更灵活的检测：检查 URL 路径中是否包含 .apk）
    const isApkUrl = (url: string): boolean => {
      try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname.toLowerCase();
        return pathname.includes('.apk') || pathname.endsWith('.apk');
      } catch {
        // 如果 URL 解析失败，使用简单的字符串检查
        return url.toLowerCase().includes('.apk');
      }
    };
    
    console.log('[UpdateDialog] 更新检查:');
    console.log('  平台:', platform);
    console.log('  URL:', updateInfo.updateUrl);
    console.log('  是否为 APK URL:', isApkUrl(updateInfo.updateUrl));
    
    // Android 平台且 URL 是 APK 链接，使用应用内更新
    if (platform === 'android' && isApkUrl(updateInfo.updateUrl)) {
      try {
        setIsDownloading(true);
        setError(null);
        setDownloadProgress(0);

        console.log('[UpdateDialog] 开始应用内更新...');

        const onProgress: DownloadProgressCallback = (progress) => {
          setDownloadProgress(progress.progress);
        };

        await downloadAndInstallApk(updateInfo.updateUrl, undefined, onProgress);
        console.log('[UpdateDialog] 应用内更新成功，安装已启动');
        // 安装已启动，关闭对话框
        if (onClose) {
          setIsVisible(false);
          setTimeout(() => onClose(), ANIMATION_DURATION_MS);
        }
      } catch (err) {
        console.error('[UpdateDialog] 应用内更新失败:', err);
        console.error('[UpdateDialog] 错误详情:', err instanceof Error ? err.stack : String(err));
        setError(err instanceof Error ? err.message : '更新失败');
        setIsDownloading(false);
        // 如果应用内更新失败，回退到浏览器下载
        console.log('[UpdateDialog] 回退到浏览器下载');
        await openUpdateUrl(updateInfo.updateUrl);
      }
    } else {
      // 非 Android 或非 APK 链接，使用浏览器下载
      console.log('[UpdateDialog] 使用浏览器下载（原因:', platform !== 'android' ? '非 Android 平台' : '非 APK 链接', ')');
      await openUpdateUrl(updateInfo.updateUrl);
    }
  };

  const handleClose = () => {
    if (onClose) {
      setIsVisible(false);
      setTimeout(() => onClose(), ANIMATION_DURATION_MS);
    }
  };

  // 强制更新时，不允许关闭
  const canClose = !updateInfo.forceUpdate && onClose;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        opacity: isVisible ? 1 : 0,
        transition: `opacity ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
      }}
    >
      {/* 背景遮罩 */}
      <div 
        className="absolute inset-0 bg-black/50 dark:bg-black/70"
        onClick={canClose ? handleClose : undefined}
        style={{
          opacity: isVisible ? 1 : 0,
          transition: `opacity ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
        }}
      />

      {/* 对话框 */}
      <div 
        className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        style={{
          transform: isVisible ? 'scale(1)' : 'scale(0.95)',
          opacity: isVisible ? 1 : 0,
          transition: `transform ${ANIMATION_DURATION_MS}ms ${IOS_EASING}, opacity ${ANIMATION_DURATION_MS}ms ${IOS_EASING}`,
        }}
      >
        {/* 顶部图标区域 - 渐变背景 */}
        <div className="bg-gradient-to-br from-blue-500 via-blue-600 to-purple-600 px-6 pt-8 pb-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <svg 
              className="w-8 h-8 text-white" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-1">
            {updateInfo.forceUpdate ? '需要更新' : '发现新版本'}
          </h2>
          {updateInfo.forceUpdate && (
            <p className="text-sm text-white/80">
              此版本需要更新才能继续使用
            </p>
          )}
        </div>

        {/* 内容区域 */}
        <div className="px-6 py-6">
          {/* 版本对比卡片 */}
          <div className="mb-6">
            <div className="flex items-center justify-between gap-4 mb-4">
              {/* 当前版本卡片 */}
              <div className="flex-1 bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">当前版本</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {updateInfo.currentVersion}
                </div>
              </div>

              {/* 箭头图标 */}
              <div className="flex-shrink-0">
                <svg 
                  className="w-6 h-6 text-blue-500 dark:text-blue-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M13 7l5 5m0 0l-5 5m5-5H6" 
                  />
                </svg>
              </div>

              {/* 最新版本卡片 */}
              <div className="flex-1 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-4 border-2 border-blue-200 dark:border-blue-700">
                <div className="text-xs text-blue-600 dark:text-blue-400 mb-1 font-medium">最新版本</div>
                <div className="text-lg font-bold text-blue-700 dark:text-blue-300">
                  {updateInfo.latestVersion}
                </div>
              </div>
            </div>
          </div>

          {/* 更新内容区域 */}
          {updateInfo.releaseNotes && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                <svg 
                  className="w-4 h-4 text-blue-500" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                </svg>
                更新内容
              </p>
              <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line leading-relaxed">
                {updateInfo.releaseNotes}
              </div>
            </div>
          )}

          {/* 错误提示 */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <div className="flex items-start gap-2">
                <svg 
                  className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                </svg>
                <p className="text-sm text-red-600 dark:text-red-400 flex-1">{error}</p>
              </div>
            </div>
          )}

          {/* 下载进度条 */}
          {isDownloading && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <svg 
                    className="w-4 h-4 text-blue-500 animate-spin" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                    />
                  </svg>
                  下载中...
                </span>
                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                  {downloadProgress}%
                </span>
              </div>
              <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                {/* 进度条 */}
                <div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${downloadProgress}%` }}
                />
                {/* 脉冲动画效果 */}
                {downloadProgress > 0 && downloadProgress < 100 && (
                  <div 
                    className="absolute top-0 left-0 h-full w-full bg-gradient-to-r from-blue-400/50 to-purple-400/50 rounded-full animate-pulse"
                    style={{ width: `${downloadProgress}%` }}
                  />
                )}
                {/* 光泽效果 */}
                <div 
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-transparent via-white/30 to-transparent rounded-full"
                  style={{ 
                    width: `${downloadProgress}%`,
                    transform: 'translateX(-100%)',
                    animation: downloadProgress > 0 ? 'shimmer 2s infinite' : 'none',
                  }}
                />
              </div>
            </div>
          )}

          {/* 按钮区域 */}
          <div className="flex flex-col sm:flex-row gap-3">
            {canClose && !isDownloading && (
              <button
                onClick={handleClose}
                className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 active:scale-[0.98] transition-all duration-150 text-gray-700 dark:text-gray-300 font-medium"
              >
                稍后
              </button>
            )}
            <button
              onClick={handleUpdate}
              disabled={isDownloading}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-all duration-150 shadow-lg shadow-blue-500/30 dark:shadow-blue-500/20 flex items-center justify-center gap-2"
            >
              {isDownloading ? (
                <>
                  <svg 
                    className="w-5 h-5 animate-spin" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                    />
                  </svg>
                  <span>下载中 {downloadProgress}%</span>
                </>
              ) : (
                <>
                  <svg 
                    className="w-5 h-5" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" 
                    />
                  </svg>
                  <span>立即更新</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 添加 shimmer 动画样式 */}
      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(200%);
          }
        }
      `}</style>
    </div>
  );
};
